import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ActionIcon, Badge, Button, Center, Container, Divider, Group, Image, Loader, Menu, Modal, Paper, ScrollArea, SimpleGrid, Space, Stack, Text, ThemeIcon, Title, useMantineTheme } from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { IconPencil, IconClipboardList, IconMapPin, IconTag, IconUser, IconDots, IconFileSearch, IconFileDownload, IconTrash, IconCopy, IconMailForward, IconPrinter, IconChevronLeft, IconExclamationMark, IconBuildingStore, IconBox, IconUsers, IconClipboardCopy } from "@tabler/icons-react";
import saveAs from "file-saver";
import moment from "moment";

import { api } from "../../api/api";
import { useGetUsersQuery } from "../../api/user.api";
import { useGetTeamsQuery } from "../../api/team.api";
import { useUpdateWorkOrderStatusMutation, useSendWorkOrderEmailMutation } from "../../api/workorder.api";
import { WorkOrderDto, WorkOrderStatus } from "../../models/workorder.models";
import { ListUserDto } from "../../models/user.models";
import { ListTeamDto } from "../../models/team.models";
import { EventSource } from "../../models/event.models";
import { getWorkOrderStatusColor, getWorkOrderStatusDescription, useWorkOrderPermissions } from "../../functions/workorder.functions";
import { WorkOrderStatusPicker } from "./WorkOrderStatus";
import { PDFDocumentPreview } from "../Shared/PDFDocumentPreview";
import SendEmailModal, { EmailType } from "../Shared/SendEmailModal";
import { CrudUserDate } from "../Shared/CrudUserDate";
import { EventList } from "../Shared/EventList";
import { CommentEntry } from "../Shared/CommentEntry";
import classes from './WorkOrderDetailView.module.css';

interface WorkOrderDetailViewProps {
	workOrder?: WorkOrderDto;
	isLoading: boolean;
	onEdit?: () => void;
	onDelete?: (workOrder: WorkOrderDto) => void;
	onCopy?: () => void;
	onClose?: () => void;
}

const PDFDocumentPrintView = ({ blobUrl, onPrinted }: { blobUrl: any, onPrinted: () => void }) => {
	const iframeRef = useRef<HTMLIFrameElement>(null);

	const handleIframeLoad = () => {
		if (iframeRef.current?.contentWindow) {
			iframeRef.current.contentWindow.print();
	 	}
	};
	
	return <>
		<iframe ref={iframeRef} onLoad={handleIframeLoad} src={blobUrl} style={{ display: 'none' }} />
	</>
}

export const WorkOrderDetailView = ({ workOrder, isLoading, onEdit, onCopy, onDelete, onClose }: WorkOrderDetailViewProps) => {
	const navigate = useNavigate();
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`, true);
	const { mutate: updateStatus, isPending: isUpdatingStatus } = useUpdateWorkOrderStatusMutation();
	const { mutate: sendEmail, isPending: isSendingEmail } = useSendWorkOrderEmailMutation();
	const [isApproving, setIsApproving] = useState(false);
	const [preview, setPreview] = useState<string | null>(null);
	const [print, setPrint] = useState<string | null>(null);

	const { data: users } = useGetUsersQuery();
	const { data: teams } = useGetTeamsQuery();

	const [emailModalVisible, { open: openEmailModal, close: closeEmailModal }] = useDisclosure(false);
	const [archiveModalVisible, { open: openArchiveModal, close: closeArchiveModal }] = useDisclosure(false);
	const { canUpdate, canUpdateStatus, canDelete, canDuplicate } = useWorkOrderPermissions(workOrder, teams);
	const isRequest = workOrder?.Status === WorkOrderStatus.Requested;
	const isCompleted = workOrder?.Status === WorkOrderStatus.Completed;
	const isArchived = workOrder?.Status === WorkOrderStatus.Archived;
	const isDeleted = workOrder?.Deleted != null;
	const isOverdue = workOrder?.DueDate != null ? moment.utc(workOrder.DueDate).isBefore(moment.utc()) && !isCompleted : false;

	useEffect(() => {
		setPreview(null);
		setPrint(null);
	}, [workOrder]);

	const handleStatusChange = (status: WorkOrderStatus) => {
		if (workOrder && status != workOrder.Status) {
			if (status === WorkOrderStatus.Archived) {
				openArchiveModal();
			} else {
				updateStatus({ workOrderId: workOrder.WorkOrderId, newStatus: status });
			}
		}
	};

	const handleConfirmArchive = () => {
		if (workOrder) {
			updateStatus({ workOrderId: workOrder.WorkOrderId, newStatus: WorkOrderStatus.Archived });
			closeArchiveModal();
		}
	};

	const handleApproveRequest = () => {
		if (workOrder) {
			setIsApproving(true);
			updateStatus({ workOrderId: workOrder.WorkOrderId, newStatus: WorkOrderStatus.Open }, {
				onSettled: () => {
					setIsApproving(false);
				}
			});
		}
	};

	const handleDeclineRequest = () => {
		if (workOrder) {
			deleteWorkOrder();
		}
	};

	const printWorkOrder = async () => {
		if (!workOrder) return;

		const response = await api.get(`/workorders/pdf/${workOrder.WorkOrderId}`, {
			responseType: 'arraybuffer',
			headers: {
				'Content-Type': 'application/json'
			}
		});

		const type = response.headers['content-type']
		const blob = new Blob([response.data], { type: type });
		const blobURL = URL.createObjectURL(blob);
		// window.open(blobURL);
		setPrint(blobURL);
	}

	const previewWorkOrder = async () => {
		if (!workOrder) return;

		setPreview(`/workorders/pdf/${workOrder.WorkOrderId}`);

		// const response = await api.get(`/workorders/pdf/${workOrder.WorkOrderId}`, {
		// 	responseType: 'arraybuffer',
		// 	headers: {
		// 		'Content-Type': 'application/json'
		// 	}
		// });

		// const type = response.headers['content-type']
		// const blob = new Blob([response.data], { type: type });
		// const blobURL = URL.createObjectURL(blob);
		// // window.open(blobURL);
		// setPreview(blobURL);
	}

	const downloadWorkOrder = async () => {
		if (!workOrder) return;
		const response = await api.get(`/workorders/pdf/${workOrder.WorkOrderId}`, {
			responseType: 'arraybuffer',
			headers: {
				'Content-Type': 'application/json'
			}
		});
		

		const type = response.headers['content-type']
		const blob = new Blob([response.data], { type: type });
		saveAs(blob, `Work Order #${workOrder.WorkOrderNumber}.pdf`);

		// const link = document.createElement('a');
		// link.href = window.URL.createObjectURL(blob);
		// link.download = `${applicationDefinition} Contract for ${profileName}.pdf`;
		// link.click();
		// document.removeChild(link);
	}

	const copyWorkOrder = () => {
		if (!workOrder) return;

		if (onCopy && workOrder)
			onCopy();
	};

	const copyToWorkOrderTemplate = () => {
		navigate(`/workordertemplates/new/workorder/${workOrder?.WorkOrderId}`);
	}

	const handleSendEmail = (users: ListUserDto[], teams: ListTeamDto[], customEmails: string[]) => {
		if (!workOrder) return;

		sendEmail({ 
			workOrderId: workOrder.WorkOrderId, 
			model: { 
				UserIds: users.map(u => u.UserId), 
				TeamIds: teams.map(t => t.TeamId), 
				CustomEmails: customEmails 
			} 
		}, {
			onSuccess: () => {
				closeEmailModal();
			}
		});
	};

	const deleteWorkOrder = () => {
		if (!workOrder) return;
		
		if (onDelete && workOrder)
			onDelete(workOrder);
	}

	if (isLoading) return <Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: 'hidden' }}>
			<ScrollArea type={isMobile ? "never" : "scroll"} style={{ height: isMobile ? 'unset' : 'calc(100vh - 195px)' }}>
				<Center>
					<Loader size="sm" mt="lg" />
				</Center>
			</ScrollArea>
		</Paper>
	if (!workOrder) return <Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: 'hidden' }}>
			<ScrollArea type={isMobile ? "never" : "scroll"} style={{ height: isMobile ? 'unset' : 'calc(100vh - 195px)' }}>
				<Space h="md" />
				<Stack justify="center" align="center" gap="md" pt="lg">
					<ThemeIcon size={100} radius={100} variant="light" color="gray">
						<IconClipboardList size={60} />
					</ThemeIcon>
					<Text fz="lg" fw="500" c="dimmed">Select a work order to view details</Text>
				</Stack>
				<Space h="md" />
			</ScrollArea>
		</Paper>

	return <>
		<Modal
			opened={archiveModalVisible}
			onClose={closeArchiveModal}
			title={<Title order={4}>Archive Work Order</Title>}
			centered
		>
			<Stack gap="md">
				<Text>
					Are you sure you want to archive this work order? Archived work orders can no longer be edited or deleted.
				</Text>
				<Group justify="flex-end" gap="sm">
					<Button variant="subtle" onClick={closeArchiveModal}>
						Cancel
					</Button>
					<Button variant="filled" onClick={handleConfirmArchive} loading={isUpdatingStatus}>
						Archive Work Order
					</Button>
				</Group>
			</Stack>
		</Modal>
		<SendEmailModal
			emailType={EmailType.WorkOrder}
			visible={emailModalVisible}
			onCancel={closeEmailModal}
			onSend={handleSendEmail}
			isSending={isSendingEmail}
			users={users ?? []}
			teams={teams ?? []}
		/>
		<Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: 'hidden' }}>
			<ScrollArea type={isMobile ? "never" : "hover"} style={{ height: isMobile ? 'calc(100vh)' : 'calc(100vh - 195px)' }}>
				<Stack gap={0} mb="md" justify="center" className={classes['header']}>
					<Group justify="space-between" px="20" wrap="nowrap">
						<Group gap="xs" wrap="nowrap">
							<ActionIcon variant="light" radius="xl" onClick={onClose} hiddenFrom="sm">
								<IconChevronLeft />
							</ActionIcon>
							<Stack gap={0}>
								<Title order={isMobile ? 6 : 3} lineClamp={3}>{workOrder?.Title}</Title>
								{isCompleted &&
								<Text fz="xs" c="dimmed" visibleFrom="sm">Completed on {moment(workOrder?.CompletedDate).format("MMMM D, YYYY")}</Text>
							}
								{!isCompleted &&
								<Text fz="xs" c="dimmed" visibleFrom="sm">Created on {moment(workOrder?.Created).format("MMMM D, YYYY")}</Text>
							}
							</Stack>
						</Group>
						<Group gap="xs">
							<Button.Group mr={-10}>
								{!isMobile &&
								<Button onClick={onEdit} disabled={!canUpdate || isDeleted} variant="subtle" radius="xl" leftSection={<IconPencil stroke={1.5} />}>Edit</Button>
								}
								{isMobile &&
								<Button onClick={onEdit} disabled={!canUpdate || isDeleted} variant="subtle" radius="xl" px="sm"><IconPencil stroke={1.5} /></Button>
								}
								<Divider orientation="vertical" />
								<Menu position="bottom-end" shadow="md" radius="lg">
									<Menu.Target>
										<Button variant="subtle" radius="xl" px="sm"><IconDots size={20} /></Button>
									</Menu.Target>
									<Menu.Dropdown>
										<Menu.Item onClick={previewWorkOrder} disabled={isDeleted || isRequest} leftSection={<IconFileSearch stroke={1.5} />}>Preview PDF</Menu.Item>
										<Menu.Item onClick={printWorkOrder} disabled={isDeleted || isRequest} leftSection={<IconPrinter stroke={1.5} />}>Print PDF</Menu.Item>
										<Menu.Item onClick={downloadWorkOrder} disabled={isDeleted || isRequest} leftSection={<IconFileDownload stroke={1.5} />}>Download PDF</Menu.Item>
										<Menu.Item onClick={openEmailModal} disabled={isDeleted || isRequest} leftSection={<IconMailForward stroke={1.5} />}>Email PDF</Menu.Item>
										{canDuplicate && <>
											<Menu.Divider />
											<Menu.Item onClick={copyWorkOrder} disabled={!canUpdate || !canDuplicate} leftSection={<IconCopy stroke={1.5} />}>Copy to new Work Order</Menu.Item>
											<Menu.Item onClick={copyToWorkOrderTemplate} disabled={!canUpdate || isDeleted} leftSection={<IconClipboardCopy stroke={1.5} />}>Copy to new Template</Menu.Item>
										</>}
										<Menu.Divider />
										<Menu.Item onClick={deleteWorkOrder} disabled={!canDelete || isDeleted} color="red" leftSection={<IconTrash stroke={1.5} />} fw="bold">Delete</Menu.Item>
									</Menu.Dropdown>
								</Menu>
							</Button.Group>
						</Group>
					</Group>
				</Stack>

				<Container px="20">

					<PDFDocumentPreview pdfUrl={preview} onClose={() => setPreview(null)} />

					{print && <>
						<PDFDocumentPrintView blobUrl={print} onPrinted={() => setPrint(null)} />
					</>}

					{!isDeleted && !isArchived && canUpdateStatus && <>
						<Stack gap="xs">
							<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Status</Text>
							<WorkOrderStatusPicker
								value={workOrder?.Status}
								onChange={handleStatusChange}
								disabled={isUpdatingStatus}
								onApprove={handleApproveRequest}
								isApproving={isApproving}
								onDecline={handleDeclineRequest}
							/>
						</Stack>
					</>}
					
					{(workOrder?.Status !== WorkOrderStatus.Requested || isArchived) && <>
					<SimpleGrid cols={isMobile ? 2 : 3} mb="md">
						{(!canUpdateStatus || isArchived) && <>
							<Stack gap="xs">
								<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Status</Text>
								<Badge size="lg" color={getWorkOrderStatusColor(workOrder?.Status)} variant="light" radius="sm">{getWorkOrderStatusDescription(workOrder?.Status)}</Badge>
							</Stack>
						</>}
						{/* <Stack gap="xs">
							<Text fz="sm" fw="bold">Status</Text>
							<Badge size="lg" color={getWorkOrderStatusColor(workOrder?.Status)} radius="sm">{getWorkOrderStatusDescription(workOrder?.Status)}</Badge>
							</Stack> */}
						<Stack gap="xs">
							<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Priority</Text>
							<Badge size="lg" color={workOrder?.Priority.Colour} variant="dot" radius="sm">{workOrder?.Priority.ShortTitle}</Badge>
						</Stack>
						<Stack gap="xs">
							<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Work Order</Text>
							<Text fz="sm">#{workOrder?.WorkOrderNumber}</Text>
						</Stack>
						{workOrder?.StartedDate && <Stack gap="xs">
							<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Start Date</Text>
							<Text fz="sm">{moment(workOrder?.StartedDate).format("MMMM D, YYYY")}</Text>
						</Stack>}
						{workOrder?.DueDate && <Stack gap="xs">
							<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Due Date</Text>
							<Group wrap="nowrap" gap="4">
								<Text fz="sm">{moment(workOrder?.DueDate).format("MMMM D, YYYY")}</Text>
								{isOverdue && <ThemeIcon color="red" variant="filled" size={16} radius="xl" style={{ marginTop: 0 }}>
									<IconExclamationMark size={16} stroke={3} />
								</ThemeIcon>}
							</Group>
						</Stack>}						
					</SimpleGrid>
					</>}
					{/* <Group justify="space-between" mb="md">
						<Stack gap="xs">
							<Text fz="sm" fw="bold">Status</Text>
							<Badge size="lg" color={getWorkOrderStatusColor(workOrder?.Status)} radius="sm">{getWorkOrderStatusDescription(workOrder?.Status)}</Badge>
						</Stack>
						<Stack gap="xs">
							<Text fz="sm" fw="bold">Priority</Text>
							<Badge size="lg" color={getWorkOrderPriorityColor(workOrder?.Priority)} variant="dot" radius="sm">{getWorkOrderPriorityDescription(workOrder?.Priority)}</Badge>
						</Stack>
						<Stack gap="xs" align="center">
							<Text fz="sm" fw="bold">Work Order</Text>
							<Text fz="sm">#{workOrder?.WorkOrderNumber}</Text>
						</Stack>
					</Group> */}

					{workOrder?.Status > WorkOrderStatus.Requested && <>
					<Divider mb="md" />
					<Stack gap="xs">
						<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Assigned To</Text>
						{((workOrder?.AssignedTo?.Users.length ?? 0) > 0 || (workOrder?.AssignedTo?.Teams.length ?? 0) > 0) && <>
							{/* {workOrder?.AssignedTo?.Teams.map(user => user.Name).join(", ")} */}
							{/* {(workOrder?.AssignedTo?.Users.length ?? 0) > 0 && (workOrder?.AssignedTo?.Users.length ?? 0) > 0 ? ", " : ""} */}
							{/* {workOrder?.AssignedTo?.Users.map(user => user.Name).join(", ")} */}
							<Group gap="sm">
								{workOrder?.AssignedTo?.Teams.map(team => (
									<Link key={team.TeamId} to={{ pathname: `/teams/${team.TeamId}` }} className={classes['link']}>
										<Badge size="lg" fz="sm" radius="sm" leftSection={<IconUsers size={14} />}>{team.Name}</Badge>
									</Link>
								))}
								{workOrder.AssignedTo?.Users.map(user => (
									<Link key={user.UserId} to={{ pathname: `/users/${user.UserId}` }} className={classes['link']}>
										<Badge size="lg" fz="sm" radius="sm" leftSection={<IconUser size={14} />}>{user.Name}</Badge>
									</Link>
								))}
							</Group>
						</>}
						{(workOrder.AssignedTo?.Users.length === 0 && workOrder.AssignedTo?.Teams.length === 0) && <Text fz="sm" c="dimmed">No users or teams assigned yet</Text>}
					</Stack>
					<Divider my="md" />
					</>}

					{workOrder?.Description && <>
						<Stack gap="xs">
							<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Description</Text>
							<Text fz="sm" dangerouslySetInnerHTML={{ __html: workOrder?.Description.replaceAll('\n', '<br />') }} />
						</Stack>
						<Divider my="md" />
					</>}

					{workOrder?.Files && workOrder.Files.filter(file => file.IsImage).length > 0 && <>
						<Stack gap="xs">
							<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Pictures</Text>
							<SimpleGrid cols={3} spacing="xs">
								{workOrder.Files.filter(file => file.IsImage).map(file => (
									<a key={file.FileId} href={file.Url} target="_blank">
										<Image src={file.Url} alt={file.Name} radius="md" style={{ objectFit: 'cover', aspectRatio: '4/3', width: '100%', height: 'auto' }} />
									</a>
								))}
							</SimpleGrid>
						</Stack>
						<Divider my="md" />
					</>}
					
					<SimpleGrid cols={isMobile ? 1 : 2} spacing="md" mb="md">
						<Stack gap="xs">
							<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Location</Text>
							<Group gap="sm">
								<Link to={{ pathname: `/locations/${workOrder?.Location.LocationId}` }} className={classes['link']}>
									<Badge size="lg" fz="sm" radius="sm" leftSection={<IconMapPin size={14} />}>{workOrder?.Location.Title}</Badge>
								</Link>
							</Group>
						</Stack>
						{isMobile && workOrder?.Location && <Divider />}

						{workOrder?.Status > WorkOrderStatus.Requested && (
							<Stack gap="xs">
								<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Categories</Text>
								<Group gap="sm">
									{workOrder?.Categories.map((category, index) => (
										<Link key={category.CategoryId} to={{ pathname: `/categories/${category.CategoryId}` }} className={classes['link']}>
											<Badge size="lg" fz="sm" radius="sm" leftSection={<IconTag size={14} />}>{category.Title}</Badge>
										</Link>
									))}
									{workOrder.Categories.length === 0 && <Text fz="sm" c="dimmed">No categories assigned yet</Text>}
								</Group>
							</Stack>
						)}
					</SimpleGrid>
					{(workOrder?.Location || (workOrder?.Categories && workOrder.Categories.length > 0)) && <Divider my="md" />}

					{(workOrder?.Assets && workOrder.Assets.length > 0) || (workOrder?.Vendors && workOrder.Vendors.length > 0) ? (
						<SimpleGrid cols={isMobile ? 1 : 2} spacing="md" mb="md">
							{workOrder?.Assets && workOrder.Assets.length > 0 && <>
								<Stack gap="xs">
									<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Assets</Text>
									<Group gap="sm">
										{workOrder?.Assets.map((asset, index) => (
											<Link key={asset.AssetId} to={{ pathname: `/assets/${asset.AssetId}` }} className={classes['link']}>
												<Badge size="lg" fz="sm" radius="sm" leftSection={<IconBox size={14} />}>{asset.Name}</Badge>
											</Link>
										))}
									</Group>
								</Stack>
								{(isMobile && workOrder?.Assets && workOrder.Assets.length > 0) && <Divider />}
							</>}

							{workOrder?.Vendors && workOrder.Vendors.length > 0 && (
								<Stack gap="xs">
									<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Vendors</Text>
									<Group gap="sm">
										{workOrder?.Vendors.map((vendor, index) => (
											<Link key={vendor.VendorId} to={{ pathname: `/vendors/${vendor.VendorId}` }} className={classes['link']}>
												<Badge size="lg" fz="sm" radius="sm" leftSection={<IconBuildingStore size={14} />}>{vendor.Name}</Badge>
											</Link>
										))}
									</Group>
								</Stack>
							)}
						</SimpleGrid>
					) : null}
					{(workOrder?.Assets && workOrder.Assets.length > 0) || (workOrder?.Vendors && workOrder.Vendors.length > 0) ? <Divider my="md" /> : null}

					<SimpleGrid cols={isMobile ? 1 : 2}>
						<CrudUserDate label="Created" userId={workOrder.CreatedBy.UserId} userName={workOrder.CreatedBy.Name} date={workOrder.Created} />
						{workOrder.UpdatedBy && !workOrder.Deleted && <>
							<CrudUserDate label="Updated" userId={workOrder.UpdatedBy.UserId} userName={workOrder.UpdatedBy.Name} date={workOrder.Updated} />
						</>}
						{workOrder.DeletedBy && <>
							<CrudUserDate label="Deleted" userId={workOrder.DeletedBy.UserId} userName={workOrder.DeletedBy.Name} date={workOrder.Deleted} />
						</>}
					</SimpleGrid>

					{workOrder?.Status > WorkOrderStatus.Requested && <>
						{!isArchived && !isDeleted ? <>
						<Space h="md" />
						<Divider mx={0} />
						<Space h="md" />
						<CommentEntry sourceId={workOrder?.WorkOrderId || ''} sourceType={EventSource.WorkOrder} />
						</> : <>
						{(isArchived || isDeleted) && <Divider mt="md" />}
						<Space h="md" />
						<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Comments</Text>
						</>}
						<Space h="sm" />
						<EventList events={workOrder.Events} commentsOnly={true} />
					</>}

					{workOrder?.Events && workOrder.Events.length > 0 && <>
						<Space h="md" />
						<Divider mx={0} />
						<Space h="md" />
						<Text fz="xs" fw="700" tt="uppercase" c="dimmed">Activity</Text>
						<Space h="sm" />
						<EventList events={workOrder.Events} />
						<Space h="md" />
					</>}
				</Container>
			</ScrollArea>
		</Paper>
	</>;
};